import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { MDSButton, MDSIcon, MDSImage, MDSTypography } from '@marqvision/mds-v2';
import { SIZE } from './@constants';

const Styled = {
  Wrapper: styled.div`
    position: relative;
    min-width: 276px;
    min-height: 107px;
    display: grid;
    grid-template-columns: 1fr auto;
  `,
  Section: styled.div`
    padding: 16px;
    display: grid;
    grid-template-rows: 1fr auto;
    justify-items: flex-start;
    gap: 8px;
  `,
  Content: styled.div`
    display: grid;
    grid-template-rows: auto 1fr;
    justify-items: flex-start;
    gap: 6px;
  `,
  Mirror: styled.div`
    position: absolute;
    visibility: hidden;
  `,
  Blank: styled.div`
    width: 28px;
  `,
  CloseButton: styled.button<{ $isClickable: boolean }>`
    top: 8px;
    right: 8px;
    position: absolute;
    padding: 4px;
    border: none;
    background-color: ${({ theme }) => theme._raw_color.whiteAlpha40};
    border-radius: 50%;
    cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  `,
};

const DELAY = 0;

type Props = {
  title: string;
  content: string;
  image?: {
    isLoading?: boolean;
    url: string;
  };
  actionLabel: string;
  onWidthChange: (width: number) => void;
  onExplorer?: () => void;
  onClose?: () => void;
};

export const ReleaseNotePopupBox = (props: Props) => {
  const { title, content, image, actionLabel, onExplorer, onClose } = props;

  const titleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const onWidthChange = useRef(props.onWidthChange);

  const contentMirrorWidth = Math.max(titleRef.current?.clientWidth || 0, SIZE.title.minWidth);

  useEffect(() => {
    const handler = window.setTimeout(() => {
      if (!titleRef.current || !contentRef.current) return;
      const titleWidth = titleRef.current.clientWidth + 1;
      const contentHeight = contentRef.current.clientHeight + 1;
      const totalHeight =
        contentHeight > SIZE.content.maxHeight
          ? SIZE.title.maxWidth
          : clamp(SIZE.title.minWidth, titleWidth, SIZE.title.maxWidth);
      const imageWidth = image?.url ? SIZE.image.width : SIZE.blank.width;
      const wrapperWidth = SIZE.padding + totalHeight + imageWidth;
      onWidthChange.current(wrapperWidth);
    }, DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [title, content, image?.url]);

  return (
    <Styled.Wrapper>
      <Styled.Section>
        <Styled.Content>
          <Styled.Mirror ref={titleRef}>
            <MDSTypography variant="title" size="s" weight="semibold" whiteSpace="nowrap" style={{ fontWeight: 640 }}>
              {title}
            </MDSTypography>
          </Styled.Mirror>
          <Styled.Mirror ref={contentRef} style={{ width: contentMirrorWidth }}>
            <MDSTypography size="s" wordBreak="break-word">
              {content}
            </MDSTypography>
          </Styled.Mirror>
          <MDSTypography
            variant="title"
            size="s"
            weight="semibold"
            lineClamp={1}
            wordBreak="break-word"
            color={title ? undefined : 'color/content/neutral/default/disabled'}
            style={{ fontWeight: 640 }}
          >
            {title || 'Title'}
          </MDSTypography>
          <MDSTypography
            size="s"
            lineClamp={2}
            wordBreak="break-word"
            color={content ? undefined : 'color/content/neutral/default/disabled'}
          >
            {content || 'Content'}
          </MDSTypography>
        </Styled.Content>
        <MDSButton variant="fill" size="small" color="blue" onClick={onExplorer}>
          {actionLabel}
        </MDSButton>
      </Styled.Section>
      {image ? (
        <MDSImage removeBorderRadius isLoading={image.isLoading} width="232px" height="131px" src={image.url} />
      ) : (
        <Styled.Blank />
      )}
      <Styled.CloseButton $isClickable={!!onClose} onClick={onClose}>
        <MDSIcon.CloseDelete variant="outline" size={16} />
      </Styled.CloseButton>
    </Styled.Wrapper>
  );
};

const clamp = (min: number, value: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};
